/**
 * Audit Integration Utilities
 * 
 * This module provides easy-to-use wrappers for integrating audit logging
 * into service operations with minimal code changes.
 */

import { AuditLogService, setAuditContext, clearAuditContext } from '@/services/audit-log.service';
import {
  AuditLogAction,
  AuditLogSeverity,
  AuditEntityType,
  PHIDataType,
  AccessReason,
} from '@/models/audit-log.model';

// ============================================================================
// CONTEXT MANAGEMENT
// ============================================================================

/**
 * Initialize audit context on user login
 */
export function initializeAuditSession(sessionId?: string): void {
  setAuditContext({
    sessionId: sessionId || `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
}

/**
 * Clear audit context on user logout
 */
export function terminateAuditSession(): void {
  clearAuditContext();
}

// ============================================================================
// SERVICE OPERATION WRAPPERS
// ============================================================================

interface AuditedOperationOptions<T> {
  action: AuditLogAction;
  entityType: AuditEntityType | string;
  entityId?: string;
  entityName?: string;
  description: string;
  containsPHI?: boolean;
  phiDataTypes?: PHIDataType[];
  accessReason?: AccessReason;
  patientId?: string;
  getEntityId?: (result: T) => string;
  getEntityName?: (result: T) => string;
  changeDetails?: {
    fieldsChanged?: string[];
    beforeValues?: Record<string, unknown>;
    afterValues?: Record<string, unknown>;
    changeType: 'create' | 'update' | 'delete' | 'access' | 'other';
  };
}

/**
 * Wrap a service operation with automatic audit logging
 */
export async function withAuditLog<T>(
  operation: () => Promise<T>,
  options: AuditedOperationOptions<T>
): Promise<T> {
  const startTime = Date.now();
  
  try {
    const result = await operation();
    
    // Get entity ID from result if needed
    const entityId = options.entityId || 
      (options.getEntityId ? options.getEntityId(result) : undefined);
    const entityName = options.entityName ||
      (options.getEntityName ? options.getEntityName(result) : undefined);
    
    // Log successful operation
    await AuditLogService.writeLog({
      action: options.action,
      severity: AuditLogSeverity.info,
      description: options.description,
      entityType: options.entityType,
      entityId,
      entityName,
      containsPHI: options.containsPHI,
      phiDataTypes: options.phiDataTypes,
      accessReason: options.accessReason,
      patientId: options.patientId,
      changeDetails: options.changeDetails,
      success: true,
      details: {
        durationMs: Date.now() - startTime,
      },
    });
    
    return result;
  } catch (error) {
    // Log failed operation
    await AuditLogService.writeLog({
      action: options.action,
      severity: AuditLogSeverity.error,
      description: `Failed: ${options.description}`,
      entityType: options.entityType,
      entityId: options.entityId,
      entityName: options.entityName,
      containsPHI: options.containsPHI,
      phiDataTypes: options.phiDataTypes,
      accessReason: options.accessReason,
      patientId: options.patientId,
      success: false,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      details: {
        durationMs: Date.now() - startTime,
      },
    });
    
    throw error;
  }
}

// ============================================================================
// PRE-BUILT AUDIT HELPERS
// ============================================================================

/**
 * Log viewing of an entity (especially useful for PHI)
 */
export async function auditView(
  entityType: AuditEntityType | string,
  entityId: string,
  entityName?: string,
  options?: {
    containsPHI?: boolean;
    phiDataTypes?: PHIDataType[];
    accessReason?: AccessReason;
    patientId?: string;
  }
): Promise<void> {
  await AuditLogService.writeLog({
    action: options?.containsPHI ? AuditLogAction.phiViewed : AuditLogAction.dataViewed,
    severity: AuditLogSeverity.info,
    description: `Viewed ${entityType}: ${entityName || entityId}`,
    entityType,
    entityId,
    entityName,
    containsPHI: options?.containsPHI || false,
    phiDataTypes: options?.phiDataTypes,
    accessReason: options?.accessReason,
    patientId: options?.patientId,
    success: true,
  });
}

/**
 * Log list/query access for an entity type
 */
export async function auditListQuery(
  entityType: AuditEntityType | string,
  descriptionOrOptions:
    | string
    | {
        queryName?: string;
        containsPHI?: boolean;
        phiDataTypes?: PHIDataType[];
        accessReason?: AccessReason;
        patientId?: string;
        resultCount?: number;
        filters?: Record<string, unknown>;
      },
  options?: {
    containsPHI?: boolean;
    phiDataTypes?: PHIDataType[];
    accessReason?: AccessReason;
    patientId?: string;
    resultCount?: number;
    filters?: Record<string, unknown>;
  }
): Promise<void> {
  const resolvedOptions =
    typeof descriptionOrOptions === 'string' ? options : descriptionOrOptions;
  const description =
    typeof descriptionOrOptions === 'string'
      ? descriptionOrOptions
      : `List query on ${entityType}${descriptionOrOptions?.queryName ? ` (${descriptionOrOptions.queryName})` : ''}`;

  await AuditLogService.writeLog({
    action: resolvedOptions?.containsPHI ? AuditLogAction.phiViewed : AuditLogAction.dataViewed,
    severity: AuditLogSeverity.info,
    description,
    entityType,
    containsPHI: resolvedOptions?.containsPHI || false,
    phiDataTypes: resolvedOptions?.phiDataTypes,
    accessReason: resolvedOptions?.accessReason,
    patientId: resolvedOptions?.patientId,
    details: {
      queryName:
        typeof descriptionOrOptions === 'string'
          ? undefined
          : descriptionOrOptions?.queryName,
      resultCount: resolvedOptions?.resultCount,
      filters: resolvedOptions?.filters,
    },
    success: true,
  });
}

/**
 * Log creation of an entity
 */
export async function auditCreate(
  entityType: AuditEntityType | string,
  entityId: string,
  entityName?: string,
  options?: {
    containsPHI?: boolean;
    phiDataTypes?: PHIDataType[];
    details?: Record<string, unknown>;
  }
): Promise<void> {
  const actionMap: Record<string, AuditLogAction> = {
    [AuditEntityType.user]: AuditLogAction.userCreated,
    [AuditEntityType.member]: AuditLogAction.memberCreated,
    [AuditEntityType.employer]: AuditLogAction.employerCreated,
    [AuditEntityType.provider]: AuditLogAction.providerCreated,
    [AuditEntityType.receipt]: AuditLogAction.receiptSubmitted,
    [AuditEntityType.benefitPlan]: AuditLogAction.benefitPlanCreated,
    [AuditEntityType.invoice]: AuditLogAction.invoiceCreated,
  };

  await AuditLogService.writeLog({
    action: actionMap[entityType] || AuditLogAction.other,
    severity: AuditLogSeverity.info,
    description: `Created ${entityType}: ${entityName || entityId}`,
    entityType,
    entityId,
    entityName,
    containsPHI: options?.containsPHI || false,
    phiDataTypes: options?.phiDataTypes,
    changeDetails: {
      changeType: 'create',
    },
    details: options?.details,
    success: true,
  });
}

/**
 * Log update of an entity
 */
export async function auditUpdate(
  entityType: AuditEntityType | string,
  entityId: string,
  entityName?: string,
  options?: {
    containsPHI?: boolean;
    phiDataTypes?: PHIDataType[];
    fieldsChanged?: string[];
    beforeValues?: Record<string, unknown>;
    afterValues?: Record<string, unknown>;
  }
): Promise<void> {
  const actionMap: Record<string, AuditLogAction> = {
    [AuditEntityType.user]: AuditLogAction.userUpdated,
    [AuditEntityType.member]: AuditLogAction.memberUpdated,
    [AuditEntityType.employer]: AuditLogAction.employerUpdated,
    [AuditEntityType.provider]: AuditLogAction.providerUpdated,
    [AuditEntityType.receipt]: AuditLogAction.receiptModified,
    [AuditEntityType.benefitPlan]: AuditLogAction.benefitPlanUpdated,
  };

  await AuditLogService.writeLog({
    action: actionMap[entityType] || AuditLogAction.other,
    severity: AuditLogSeverity.info,
    description: `Updated ${entityType}: ${entityName || entityId}`,
    entityType,
    entityId,
    entityName,
    containsPHI: options?.containsPHI || false,
    phiDataTypes: options?.phiDataTypes,
    changeDetails: {
      changeType: 'update',
      fieldsChanged: options?.fieldsChanged,
      beforeValues: options?.beforeValues,
      afterValues: options?.afterValues,
    },
    success: true,
  });
}

/**
 * Log deletion of an entity
 */
export async function auditDelete(
  entityType: AuditEntityType | string,
  entityId: string,
  entityName?: string,
  options?: {
    containsPHI?: boolean;
    phiDataTypes?: PHIDataType[];
  }
): Promise<void> {
  const actionMap: Record<string, AuditLogAction> = {
    [AuditEntityType.user]: AuditLogAction.userDeleted,
    [AuditEntityType.member]: AuditLogAction.memberDeleted,
    [AuditEntityType.employer]: AuditLogAction.employerDeleted,
    [AuditEntityType.provider]: AuditLogAction.providerDeleted,
    [AuditEntityType.receipt]: AuditLogAction.receiptDeleted,
    [AuditEntityType.benefitPlan]: AuditLogAction.benefitPlanDeleted,
  };

  await AuditLogService.writeLog({
    action: actionMap[entityType] || AuditLogAction.dataDeleted,
    severity: AuditLogSeverity.warning,
    description: `Deleted ${entityType}: ${entityName || entityId}`,
    entityType,
    entityId,
    entityName,
    containsPHI: options?.containsPHI || false,
    phiDataTypes: options?.phiDataTypes,
    changeDetails: {
      changeType: 'delete',
    },
    success: true,
  });
}

/**
 * Log status change of an entity
 */
export async function auditStatusChange(
  entityType: AuditEntityType | string,
  entityId: string,
  entityName: string | undefined,
  oldStatus: string | number,
  newStatus: string | number
): Promise<void> {
  const actionMap: Record<string, AuditLogAction> = {
    [AuditEntityType.user]: AuditLogAction.userStatusChanged,
    [AuditEntityType.employer]: AuditLogAction.employerStatusChanged,
    [AuditEntityType.receipt]: AuditLogAction.receiptModified,
  };

  await AuditLogService.writeLog({
    action: actionMap[entityType] || AuditLogAction.other,
    severity: AuditLogSeverity.info,
    description: `Status changed for ${entityType}: ${entityName || entityId} (${oldStatus} → ${newStatus})`,
    entityType,
    entityId,
    entityName,
    changeDetails: {
      changeType: 'update',
      fieldsChanged: ['status'],
      beforeValues: { status: oldStatus },
      afterValues: { status: newStatus },
    },
    success: true,
  });
}

/**
 * Log receipt approval/rejection
 */
export async function auditReceiptDecision(
  receiptId: string,
  decision: 'approved' | 'rejected',
  memberId: string,
  amount?: number,
  reason?: string
): Promise<void> {
  await AuditLogService.writeLog({
    action: decision === 'approved' 
      ? AuditLogAction.receiptApproved 
      : AuditLogAction.receiptRejected,
    severity: AuditLogSeverity.info,
    description: `Receipt ${decision}: $${amount?.toFixed(2) || '0.00'}${reason ? ` - ${reason}` : ''}`,
    entityType: AuditEntityType.receipt,
    entityId: receiptId,
    containsPHI: true,
    phiDataTypes: [PHIDataType.billingRecord, PHIDataType.healthRecord],
    accessReason: AccessReason.payment,
    patientId: memberId,
    details: {
      decision,
      amount,
      reason,
    },
    success: true,
  });
}

/**
 * Log bulk operation
 */
export async function auditBulkOperation(
  operationType: string,
  entityType: AuditEntityType | string,
  affectedCount: number,
  affectedIds?: string[],
  containsPHI?: boolean
): Promise<void> {
  await AuditLogService.writeLog({
    action: AuditLogAction.bulkOperation,
    severity: containsPHI ? AuditLogSeverity.warning : AuditLogSeverity.info,
    description: `Bulk ${operationType}: ${affectedCount} ${entityType} records`,
    entityType,
    containsPHI: containsPHI || false,
    details: {
      operationType,
      affectedCount,
      affectedIds: affectedIds?.slice(0, 100), // Limit for storage
    },
    success: true,
  });
}

/**
 * Log settings change
 */
export async function auditSettingsChange(
  settingCategory: string,
  settingName: string,
  oldValue: unknown,
  newValue: unknown
): Promise<void> {
  await AuditLogService.writeLog({
    action: AuditLogAction.settingsChanged,
    severity: AuditLogSeverity.warning,
    description: `Settings changed: ${settingCategory}.${settingName}`,
    entityType: AuditEntityType.system,
    entityName: `${settingCategory}.${settingName}`,
    changeDetails: {
      changeType: 'update',
      fieldsChanged: [settingName],
      beforeValues: { [settingName]: oldValue },
      afterValues: { [settingName]: newValue },
    },
    success: true,
  });
}

// ============================================================================
// AUTHENTICATION HELPERS
// ============================================================================

/**
 * Log successful login
 */
export async function auditLoginSuccess(
  userId: string,
  userEmail: string,
  userName?: string
): Promise<void> {
  await AuditLogService.logLogin(userId, userEmail, userName, true);
}

/**
 * Log failed login
 */
export async function auditLoginFailure(
  userEmail: string,
  reason: string
): Promise<void> {
  await AuditLogService.logLogin('', userEmail, undefined, false, reason);
}

/**
 * Log logout
 */
export async function auditLogout(
  userId: string,
  userEmail: string
): Promise<void> {
  await AuditLogService.logLogout(userId, userEmail);
}

/**
 * Log password change
 */
export async function auditPasswordChange(
  userId: string,
  userEmail: string,
  initiatedBy: 'user' | 'admin' | 'system'
): Promise<void> {
  await AuditLogService.writeLog({
    action: AuditLogAction.passwordChanged,
    severity: AuditLogSeverity.warning,
    description: `Password changed for ${userEmail} (initiated by ${initiatedBy})`,
    entityType: AuditEntityType.user,
    entityId: userId,
    entityName: userEmail,
    details: {
      initiatedBy,
    },
    success: true,
  });
}

// ============================================================================
// SECURITY HELPERS
// ============================================================================

/**
 * Log unauthorized access attempt
 */
export async function auditUnauthorizedAccess(
  resource: string,
  userId?: string,
  userEmail?: string,
  reason?: string
): Promise<void> {
  await AuditLogService.logUnauthorizedAccess(resource, userId, userEmail, { reason });
}

/**
 * Log permission denied
 */
export async function auditPermissionDenied(
  resource: string,
  requiredPermission: string,
  userId?: string
): Promise<void> {
  await AuditLogService.writeLog({
    action: AuditLogAction.permissionDenied,
    severity: AuditLogSeverity.warning,
    description: `Permission denied: ${requiredPermission} required for ${resource}`,
    userId,
    entityType: AuditEntityType.system,
    entityName: resource,
    details: {
      requiredPermission,
    },
    success: false,
  });
}

/**
 * Log suspicious activity
 */
export async function auditSuspiciousActivity(
  description: string,
  userId?: string,
  details?: Record<string, unknown>
): Promise<void> {
  await AuditLogService.writeLog({
    action: AuditLogAction.suspiciousActivity,
    severity: AuditLogSeverity.critical,
    description: `Suspicious activity: ${description}`,
    userId,
    entityType: AuditEntityType.system,
    details,
    success: false,
  });
}

// ============================================================================
// EXPORT HELPERS
// ============================================================================

/**
 * Log data export
 */
export async function auditExport(
  exportType: string,
  recordCount: number,
  format: string,
  containsPHI: boolean = false
): Promise<void> {
  await AuditLogService.logDataExport(exportType, recordCount, containsPHI, format);
}

// Re-export the service for advanced usage
export { AuditLogService };
